import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config';
import { subscribeUserToPush } from '../utils/pushNotifications';

const SOCKET_URL = API_BASE_URL.startsWith('http')
    ? API_BASE_URL.replace('/api', '')
    : (window.location.hostname === 'localhost' ? 'http://localhost:4000' : window.location.origin);

const AdminSocketContext = createContext(null);

export const useAdminSocket = () => useContext(AdminSocketContext);

// Helper to play a clean 'beep' sound using browser standards (no files needed)
let audioCtx = null;
const playNotificationSound = () => {
    try {
        if (!audioCtx) {
            audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        }
        if (audioCtx.state === 'suspended') {
            audioCtx.resume();
        }

        const oscillator = audioCtx.createOscillator();
        const gainNode = audioCtx.createGain();

        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(587.33, audioCtx.currentTime); // D5
        oscillator.frequency.exponentialRampToValueAtTime(880, audioCtx.currentTime + 0.1); // Slide up

        gainNode.gain.setValueAtTime(0, audioCtx.currentTime);
        // Increased max volume to 0.4 (from 0.15) for louder notification
        gainNode.gain.linearRampToValueAtTime(0.4, audioCtx.currentTime + 0.02);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.3);

        oscillator.connect(gainNode);
        gainNode.connect(audioCtx.destination);

        oscillator.start();
        oscillator.stop(audioCtx.currentTime + 0.3);
    } catch (e) {
        console.log('[AdminSocket] Audio blocked or unsupported', e);
    }
};

export const AdminSocketProvider = ({ children }) => {
    const [socket, setSocket] = useState(null);
    const [isOfflineMode, setIsOfflineMode] = useState(false);
    const [onlineClients, setOnlineClients] = useState(new Set()); // IDs of clients currently connected
    const [unreadSessions, setUnreadSessions] = useState(new Set());

    useEffect(() => {
        // Fetch initial offline mode status
        const fetchOfflineMode = async () => {
            try {
                const res = await axios.get(`${API_BASE_URL}/chats/offline-mode`, { withCredentials: true });
                setIsOfflineMode(res.data.isOfflineMode);
            } catch (error) {
                console.error('Failed to fetch offline mode status', error);
            }
        };
        fetchOfflineMode();

        // Fetch initial unread count or sessions to determine initial unread status
        const fetchInitialUnread = async () => {
            try {
                const res = await axios.get(`${API_BASE_URL}/chats/sessions`, { withCredentials: true });
                const unread = new Set();
                res.data.forEach(s => {
                    const seenId = localStorage.getItem(`chat_seen_${s.id}`);
                    const lastMsgId = s.lastMessage ? String(s.lastMessage.id) : null;
                    if (s.lastMessage && s.lastMessage.senderType === 'client' && !s.isResponded && seenId !== lastMsgId) {
                        unread.add(s.id);
                    }
                });
                setUnreadSessions(unread);
            } catch (error) {
                console.error('Failed to fetch initial unread sessions', error);
            }
        };
        fetchInitialUnread();

        // Subscribe operator to push notifications
        const subscribeAdmin = async () => {
            try {
                const res = await axios.get(`${API_BASE_URL}/users/me`, { withCredentials: true });
                if (res.data && res.data.id) {
                    await subscribeUserToPush(null, res.data.id);
                }
            } catch (err) {
                console.error('Failed to subscribe operator to push notifications', err);
            }
        };
        subscribeAdmin();

        const newSocket = io(SOCKET_URL, {
            path: '/api/socket.io',
            withCredentials: true,
            transports: ['websocket', 'polling']
        });

        newSocket.on('connect', () => {
            newSocket.emit('join_admin'); // Subscribe to admin events
        });

        newSocket.on('chat_updated', ({ sessionId, message }) => {
            // Play sound for incoming client messages everywhere in the admin panel
            if (message && message.senderType === 'client') {
                playNotificationSound();
                // Add to unreadSessions if from client
                setUnreadSessions(prev => new Set([...prev, sessionId]));
            }
        });

        newSocket.on('new_session', (session) => {
            // New incoming session is always unread
            setUnreadSessions(prev => new Set([...prev, session.id]));
        });

        newSocket.on('chat_offline_mode_changed', (data) => {
            setIsOfflineMode(data.isOfflineMode);
        });

        // Track client presence
        newSocket.on('client_presence_changed', (data) => {
            setOnlineClients(prev => {
                const next = new Set(prev);
                if (data.status === 'online') {
                    next.add(data.clientId);
                } else {
                    next.delete(data.clientId);
                }
                return next;
            });
        });

        setSocket(newSocket);

        return () => {
            newSocket.close();
        };
    }, []);

    const location = useLocation();

    // Update document title for unread notifications
    useEffect(() => {
        const baseTitle = document.title.replace(/^\(\d+\)\s+/, '');

        if (unreadSessions.size > 0) {
            document.title = `(${unreadSessions.size}) ${baseTitle}`;
        } else {
            document.title = baseTitle;
        }
    }, [unreadSessions, location]);

    const markSessionAsRead = useCallback((sessionId) => {
        setUnreadSessions(prev => {
            if (!prev.has(sessionId)) return prev;
            const next = new Set(prev);
            next.delete(sessionId);
            return next;
        });
    }, []);

    const markSessionAsUnread = useCallback((sessionId) => {
        setUnreadSessions(prev => {
            if (prev.has(sessionId)) return prev;
            const next = new Set(prev);
            next.add(sessionId);
            return next;
        });
    }, []);

    const toggleOfflineMode = async () => {
        try {
            const newValue = !isOfflineMode;
            setIsOfflineMode(newValue); // Optimistic UI
            await axios.post(`${API_BASE_URL}/chats/offline-mode`, { isOfflineMode: newValue }, { withCredentials: true });
        } catch (error) {
            console.error('Failed to update offline mode', error);
            setIsOfflineMode(!isOfflineMode); // Revert on failure
        }
    };

    const enableAudioContext = () => {
        if (audioCtx && audioCtx.state === 'suspended') {
            audioCtx.resume();
        }
    };

    return (
        <AdminSocketContext.Provider value={{
            socket,
            isOfflineMode,
            toggleOfflineMode,
            enableAudioContext,
            onlineClients,
            unreadSessions,
            markSessionAsRead,
            markSessionAsUnread
        }}>
            <div onClick={enableAudioContext} style={{ height: '100%', width: '100%' }}>
                {children}
            </div>
        </AdminSocketContext.Provider>
    );
};
